import { useCallback, useEffect, useState } from 'react'
import { ClipboardList, Loader2, Plus, Trash2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  TIPO_CAMPO_LABEL, TIPO_ITEM_LABEL,
  type Pergunta, type ProtocoloItem, type TipoCampoPergunta, type TipoItemProtocolo,
} from '@/lib/types'
import {
  atualizarProtocolo, criarProtocolo, listProtocolos, novaVersaoRascunho,
  publicarVersao, salvarItensRascunho, type ProtocoloComVersao,
} from '@/services/protocolos'

function novoId(): string {
  return crypto.randomUUID().slice(0, 8)
}

export default function ProtocolosPage() {
  const { current, pode } = useAuth()
  const [protocolos, setProtocolos] = useState<ProtocoloComVersao[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // editor
  const [editando, setEditando] = useState<ProtocoloComVersao | null>(null)
  const [versaoId, setVersaoId] = useState<string | null>(null)
  const [nome, setNome] = useState('')
  const [tipoConsulta, setTipoConsulta] = useState<'primeira' | 'retorno' | 'ambas'>('ambas')
  const [itens, setItens] = useState<ProtocoloItem[]>([])
  const [criando, setCriando] = useState(false)

  const podeGerir = pode('protocolos.gerir')

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      setProtocolos(await listProtocolos(current.workspace.id))
    } catch {
      setErro('Não foi possível carregar. A migração phase4 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  async function abrirEditor(p: ProtocoloComVersao) {
    if (!current) return
    let rascunho = p.rascunho
    if (!rascunho) {
      rascunho = await novaVersaoRascunho({ workspaceId: current.workspace.id, protocolo: p })
    }
    setEditando(p)
    setVersaoId(rascunho.id)
    setNome(p.nome)
    setTipoConsulta(p.tipo_consulta)
    setItens(structuredClone(rascunho.itens ?? []))
  }

  async function criar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || nome.trim().length < 3) return
    setBusy(true)
    try {
      await criarProtocolo({ workspaceId: current.workspace.id, nome, tipoConsulta })
      setCriando(false)
      setNome('')
      const lista = await listProtocolos(current.workspace.id)
      setProtocolos(lista)
      const novo = lista.find(p => p.nome === nome.trim())
      if (novo) await abrirEditor(novo)
    } catch {
      setErro('Não foi possível criar (verifique a permissão protocolos.gerir).')
    } finally {
      setBusy(false)
    }
  }

  async function salvar(publicar: boolean) {
    if (!current || !editando || !versaoId) return
    setBusy(true)
    setErro(null)
    try {
      await atualizarProtocolo({
        workspaceId: current.workspace.id,
        id: editando.id,
        dados: { nome: nome.trim(), tipo_consulta: tipoConsulta },
      })
      await salvarItensRascunho({ workspaceId: current.workspace.id, versaoId, itens })
      if (publicar) {
        await publicarVersao({ workspaceId: current.workspace.id, protocoloId: editando.id, versaoId })
        setEditando(null)
      }
      await carregar()
    } catch {
      setErro('Não foi possível salvar.')
    } finally {
      setBusy(false)
    }
  }

  // ---- manipulação de itens/perguntas (imutável, direto no estado) ----
  function addItem(tipo: TipoItemProtocolo) {
    setItens(prev => [...prev, {
      id: novoId(), tipo, titulo: '', obrigatorio: tipo !== 'orientacao', bloqueia: false,
      ...(tipo === 'questionario' ? { perguntas: [] } : {}),
      ...(tipo === 'orientacao' ? { texto: '' } : {}),
    }])
  }
  function patchItem(id: string, patch: Partial<ProtocoloItem>) {
    setItens(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)))
  }
  function delItem(id: string) {
    setItens(prev => prev.filter(i => i.id !== id))
  }
  function addPergunta(itemId: string) {
    setItens(prev => prev.map(i => i.id === itemId
      ? { ...i, perguntas: [...(i.perguntas ?? []), { id: novoId(), tipo: 'texto_curto' as TipoCampoPergunta, rotulo: '', obrigatoria: false }] }
      : i))
  }
  function patchPergunta(itemId: string, pid: string, patch: Partial<Pergunta>) {
    setItens(prev => prev.map(i => i.id === itemId
      ? { ...i, perguntas: (i.perguntas ?? []).map(p => (p.id === pid ? { ...p, ...patch } : p)) }
      : i))
  }
  function delPergunta(itemId: string, pid: string) {
    setItens(prev => prev.map(i => i.id === itemId
      ? { ...i, perguntas: (i.perguntas ?? []).filter(p => p.id !== pid) }
      : i))
  }

  if (editando) {
    return (
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>Editar protocolo</h1>
        <p className="page-subtitle" style={{ margin: '0 0 16px' }}>
          Editando rascunho — a versão publicada continua valendo até você publicar.
        </p>

        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="pr-nome">Nome *</label>
              <input id="pr-nome" className="field-input" value={nome} onChange={e => setNome(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="pr-tipo">Aplica-se a</label>
              <select id="pr-tipo" className="field-input" value={tipoConsulta} onChange={e => setTipoConsulta(e.target.value as typeof tipoConsulta)}>
                <option value="ambas">Primeira consulta e retorno</option>
                <option value="primeira">Só primeira consulta</option>
                <option value="retorno">Só retorno</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {itens.map(item => (
              <div key={item.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  <span className="badge badge-info">{TIPO_ITEM_LABEL[item.tipo]}</span>
                  <input
                    className="field-input" style={{ flex: 1, minWidth: 180, minHeight: 38 }}
                    placeholder="Título do item (ex.: Pré-anamnese ginecológica)"
                    value={item.titulo} onChange={e => patchItem(item.id, { titulo: e.target.value })}
                  />
                  <label style={{ fontSize: 12.5, display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={item.obrigatorio} onChange={e => patchItem(item.id, { obrigatorio: e.target.checked })} />
                    Obrigatório
                  </label>
                  <label style={{ fontSize: 12.5, display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={item.bloqueia} onChange={e => patchItem(item.id, { bloqueia: e.target.checked })} />
                    Bloqueia consulta
                  </label>
                  <button type="button" className="btn-secondary" style={{ minHeight: 32, padding: '4px 8px' }} onClick={() => delItem(item.id)} aria-label="Remover item">
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>

                {item.tipo === 'orientacao' && (
                  <textarea
                    className="field-input" rows={2} style={{ resize: 'vertical' }}
                    placeholder="Texto da orientação para a paciente"
                    value={item.texto ?? ''} onChange={e => patchItem(item.id, { texto: e.target.value })}
                  />
                )}

                {item.tipo === 'questionario' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(item.perguntas ?? []).map((p, idx) => {
                      const anteriores = (item.perguntas ?? []).slice(0, idx).filter(a => a.tipo === 'sim_nao' || a.tipo === 'selecao_unica')
                      return (
                        <div key={p.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <select
                              className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13 }}
                              value={p.tipo} onChange={e => patchPergunta(item.id, p.id, { tipo: e.target.value as TipoCampoPergunta })}
                              aria-label="Tipo do campo"
                            >
                              {(Object.keys(TIPO_CAMPO_LABEL) as TipoCampoPergunta[]).map(t => (
                                <option key={t} value={t}>{TIPO_CAMPO_LABEL[t]}</option>
                              ))}
                            </select>
                            <input
                              className="field-input" style={{ flex: 1, minWidth: 160, minHeight: 34, fontSize: 13.5 }}
                              placeholder="Pergunta (ex.: Histórico familiar de câncer de mama?)"
                              value={p.rotulo} onChange={e => patchPergunta(item.id, p.id, { rotulo: e.target.value })}
                            />
                            <label style={{ fontSize: 12.5, display: 'flex', gap: 4, alignItems: 'center' }}>
                              <input type="checkbox" checked={p.obrigatoria} onChange={e => patchPergunta(item.id, p.id, { obrigatoria: e.target.checked })} />
                              Obrigatória
                            </label>
                            <button type="button" className="btn-secondary" style={{ minHeight: 30, padding: '2px 8px' }} onClick={() => delPergunta(item.id, p.id)} aria-label="Remover pergunta">
                              <Trash2 size={13} aria-hidden />
                            </button>
                          </div>
                          {(p.tipo === 'selecao_unica' || p.tipo === 'multipla_selecao') && (
                            <input
                              className="field-input" style={{ minHeight: 34, fontSize: 13 }}
                              placeholder="Opções separadas por vírgula (ex.: Nunca, Às vezes, Sempre)"
                              value={(p.opcoes ?? []).join(', ')}
                              onChange={e => patchPergunta(item.id, p.id, { opcoes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                            />
                          )}
                          {anteriores.length > 0 && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
                              <span style={{ color: 'var(--muted)' }}>Mostrar somente se:</span>
                              <select
                                className="field-input" style={{ width: 'auto', minHeight: 30, fontSize: 12.5 }}
                                value={p.condicao?.pergunta_id ?? ''}
                                onChange={e => {
                                  const pid = e.target.value
                                  patchPergunta(item.id, p.id, { condicao: pid ? { pergunta_id: pid, valor: p.condicao?.valor ?? 'sim' } : undefined })
                                }}
                                aria-label="Pergunta condicionante"
                              >
                                <option value="">(sempre visível)</option>
                                {anteriores.map(a => <option key={a.id} value={a.id}>&ldquo;{a.rotulo || a.id}&rdquo;</option>)}
                              </select>
                              {p.condicao && (
                                <>
                                  <span style={{ color: 'var(--muted)' }}>=</span>
                                  <input
                                    className="field-input" style={{ width: 110, minHeight: 30, fontSize: 12.5 }}
                                    value={p.condicao.valor}
                                    onChange={e => patchPergunta(item.id, p.id, { condicao: { pergunta_id: p.condicao!.pergunta_id, valor: e.target.value } })}
                                    placeholder="sim"
                                  />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    <button type="button" className="btn-secondary" style={{ minHeight: 34, fontSize: 13, alignSelf: 'flex-start' }} onClick={() => addPergunta(item.id)}>
                      <Plus size={13} aria-hidden /> Pergunta
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary" onClick={() => addItem('questionario')}><Plus size={14} aria-hidden /> Questionário</button>
            <button type="button" className="btn-secondary" onClick={() => addItem('checklist')}><Plus size={14} aria-hidden /> Checklist</button>
            <button type="button" className="btn-secondary" onClick={() => addItem('orientacao')}><Plus size={14} aria-hidden /> Orientação</button>
          </div>

          {erro && <div className="field-error" role="alert">{erro}</div>}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn-primary" disabled={busy} onClick={() => salvar(true)}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Publicar versão
            </button>
            <button className="btn-secondary" disabled={busy} onClick={() => salvar(false)}>Salvar rascunho</button>
            <button className="btn-secondary" disabled={busy} onClick={() => { setEditando(null); carregar() }}>Fechar</button>
          </div>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    )
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Protocolos</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Como cada tipo de consulta deve ser preparada.</p>
        </div>
        {podeGerir && (
          <button className="btn-primary" onClick={() => { setCriando(v => !v); setNome('') }}>
            <Plus size={16} aria-hidden /> Novo protocolo
          </button>
        )}
      </header>

      {criando && (
        <form onSubmit={criar} className="card" style={{ padding: 18, marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label className="field-label" htmlFor="np-nome">Nome *</label>
            <input id="np-nome" required className="field-input" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex.: Primeira consulta ginecológica" />
          </div>
          <div>
            <label className="field-label" htmlFor="np-tipo">Aplica-se a</label>
            <select id="np-tipo" className="field-input" value={tipoConsulta} onChange={e => setTipoConsulta(e.target.value as typeof tipoConsulta)}>
              <option value="ambas">Ambas</option>
              <option value="primeira">Primeira consulta</option>
              <option value="retorno">Retorno</option>
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>Criar e editar</button>
        </form>
      )}

      {erro && !criando && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      ) : protocolos.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <ClipboardList size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhum protocolo ainda</div>
          <div style={{ fontSize: 13.5, marginTop: 4 }}>Crie o primeiro — ex.: &ldquo;Primeira consulta ginecológica&rdquo;.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {protocolos.map(p => (
            <div key={p.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{p.nome}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {p.tipo_consulta === 'ambas' ? 'Primeira e retorno' : p.tipo_consulta === 'primeira' ? 'Primeira consulta' : 'Retorno'}
                  {p.versao_atual ? ` · v${p.versao_atual.numero} publicada` : ' · nunca publicado'}
                  {p.rascunho ? ' · rascunho em edição' : ''}
                </div>
              </div>
              {!p.ativo && <span className="badge badge-muted">Inativo</span>}
              {p.versao_atual ? <span className="badge badge-ok">Ativo</span> : <span className="badge badge-warn">Rascunho</span>}
              {podeGerir && (
                <button className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }} onClick={() => abrirEditor(p)}>
                  Editar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
